import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UpdateFederationInput } from '@proref/schemas';
import {
  createFederation,
  listFederations,
  updateFederation,
  deleteFederation,
} from '../services/federations.service';

// All federations (super_admin is global — no scoping). Key is ['federations'] per convention.
// `enabled` defaults to true (existing super_admin pages don't need to pass anything); set
// it to false to skip the call for roles that don't have access to /federations.
export function useFederationsQuery(enabled = true) {
  return useQuery({
    queryKey: ['federations'],
    queryFn: listFederations,
    enabled,
    // Admin reference data — tenants are created rarely, so a few minutes of staleness is fine.
    staleTime: 5 * 60_000,
  });
}

// Create a federation, then refresh the list so the new tenant appears immediately.
export function useCreateFederation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createFederation,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['federations'] });
    },
  });
}

// Edit a federation, then refresh the list.
export function useUpdateFederation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateFederationInput }) =>
      updateFederation(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['federations'] });
    },
  });
}

// Delete a federation, then refresh the list so it drops out immediately.
export function useDeleteFederation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteFederation,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['federations'] });
    },
  });
}
